import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import axiosInstant from "../../../api";
import { MainContainer } from "../../../components/ContentWrapper";
import { WhiteBackground } from "../../../components/WhiteBackground";
import { ListPageHeader } from "../../../components/ListPageHeader/ListPageHeader";
import { DataTable, DataTableColumn } from "../../../components/DataTable/DataTable";
import { useCommonParams } from "../../../hooks/useCommonParams";
import { useResourceList } from "../../../hooks/useResourceList";
import { adminBrandUsageEndpoint, adminBrandUsageEventsEndpoint } from "../../../api/endpoints";
import { formatCurrency } from "../../../utils/formatCurrency";
import { formatProductCap, intervalLabel, planLabel } from "../../../utils/plans";
import type { BrandUsageEvent, BrandUsageSeriesResponse, UsageKind } from "../../../types/pricingPlans";
import { UsagePeriodPicker, useUsagePeriod } from "./UsagePeriodPicker";

// One brand's usage: plan header, monthly roll-up, and the ledger itself —
// one row per PDP audit / score, catalog report synthesis and prep pack,
// with what it was about and what it cost vs. what it bills at.
const PAGE_SIZE = 50;

const KIND_LABELS: Record<UsageKind, string> = {
    audit: "PDP audit",
    catalog_report: "Report synthesis",
    prep_pack: "Prep pack",
    consumer_voice: "Consumer Voice",
    consumer_voice_report: "Consumer Voice synthesis",
    ai_presence: "AI Presence",
    ai_presence_standalone: "AI Presence (standalone)",
};

const TRIGGER_LABELS: Record<string, string> = {
    cron: "Scheduled",
    on_add: "On add",
    user_request: "Requested",
    internal_rescore: "Internal rescore",
    report: "Report",
    rmn_audit: "Portal audit",
    public_audit: "Public audit",
    generate: "Generated",
    regenerate: "Regenerated",
    backfill: "Backfill",
};

const money = (n: number) => (n === 0 ? "—" : formatCurrency(n));
const money4 = (n: number) => (n === 0 ? "—" : `$${n.toFixed(4).replace(/0+$/, "").replace(/\.$/, "")}`);

export default function BrandUsageDetailPage() {
    const { id } = useParams();
    const brandId = Number(id);
    const navigate = useNavigate();
    const { searchParams, setSearchParams, page, setPage, getFilter, setFilter } = useCommonParams();
    const { extraParams } = useUsagePeriod();
    const kind = getFilter("kind") || "";
    const [summary, setSummary] = useState<BrandUsageSeriesResponse | null>(null);

    useEffect(() => {
        if (!brandId) return;
        axiosInstant
            .get(adminBrandUsageEndpoint(brandId), { params: { months: 6 } })
            .then((res) => setSummary(res.data))
            .catch(() => setSummary(null));
    }, [brandId]);

    const list = useResourceList<BrandUsageEvent>({
        endpoint: adminBrandUsageEventsEndpoint(brandId),
        pageSize: PAGE_SIZE,
        searchText: "",
        searchParams,
        setSearchParams,
        page,
        setPage,
        extraParams: { ...extraParams, kind: kind || undefined },
        errorLabel: "Failed to load usage events",
    });

    const columns: DataTableColumn<BrandUsageEvent>[] = [
        {
            key: "when",
            header: "When",
            className: "text-text-2 text-[12px] font-mono whitespace-nowrap",
            render: (e) => dayjs(e.occurred_at).format("MMM DD, YYYY h:mm A"),
        },
        {
            key: "kind",
            header: "Kind",
            render: (e) => (
                <span className="font-bold uppercase border border-border-token bg-surface-2 text-text-2 text-[10.5px] rounded-md px-1.5 py-0.5 whitespace-nowrap">
                    {KIND_LABELS[e.kind] || e.kind}
                </span>
            ),
        },
        {
            key: "detail",
            header: "What",
            render: (e) => (
                <div className="min-w-0">
                    <div className="font-semibold text-text-1 truncate max-w-[420px]" title={e.detail.label}>
                        {e.detail.label}
                    </div>
                    <div className="text-[11.5px] text-text-2 truncate max-w-[420px] flex gap-2">
                        {e.detail.url && (
                            <a
                                href={e.detail.url}
                                target="_blank"
                                rel="noreferrer"
                                className="truncate hover:text-accent-text"
                                onClick={(ev) => ev.stopPropagation()}
                            >
                                {e.detail.url}
                            </a>
                        )}
                        {e.detail.score !== undefined && e.detail.score !== null && (
                            <span className="shrink-0">score {e.detail.score}</span>
                        )}
                        {e.detail.product_count !== undefined && (
                            <span className="shrink-0">{e.detail.product_count} products</span>
                        )}
                        {e.subject_brand && <span className="shrink-0">· {e.subject_brand.name}</span>}
                    </div>
                </div>
            ),
        },
        {
            key: "trigger",
            header: "Trigger",
            className: "text-text-2 text-[12px]",
            render: (e) => (e.trigger ? TRIGGER_LABELS[e.trigger] || e.trigger : "—"),
        },
        {
            key: "model",
            header: "Model",
            className: "text-text-2 text-[12px] font-mono",
            render: (e) => (
                <span title={`${e.input_tokens.toLocaleString()} in / ${e.output_tokens.toLocaleString()} out tokens`}>
                    {e.model || e.provider || "—"}
                    {e.cached ? " (cached)" : ""}
                </span>
            ),
        },
        {
            key: "actual_cost",
            header: "Our cost",
            className: "text-right font-mono",
            render: (e) =>
                e.scrape_cost ? (
                    <span title={`AI ${money4(e.actual_cost - e.scrape_cost)} · Scraping ${money4(e.scrape_cost)} (${e.scrape_credits || 0} credits)`}>
                        {money4(e.actual_cost)}
                    </span>
                ) : (
                    money4(e.actual_cost)
                ),
        },
        {
            key: "modeled_cost",
            header: "Modeled",
            className: "text-right font-mono text-text-2",
            render: (e) => money4(e.modeled_cost),
        },
        {
            key: "price",
            header: "Price",
            className: "text-right font-mono font-semibold",
            render: (e) => money(e.price),
        },
    ];

    const brand = summary?.brand;

    return (
        <WhiteBackground>
            <MainContainer>
                <ListPageHeader
                    title={brand ? `${brand.name} — Usage` : "Brand Usage"}
                    subtitle="Every PDP audit, score, report synthesis and prep pack billed to this brand"
                    secondaryActionLabel="All brands"
                    onSecondaryActionClick={() => navigate("/admin/brands/usage")}
                    actionLabel="Brand details"
                    onActionClick={() => navigate(`/brands/${brandId}/details`)}
                    titleExtra={
                        brand ? (
                            <span className="text-[12px] text-text-2 flex flex-wrap gap-x-3">
                                <span>{planLabel(brand.plan)}</span>
                                <span>
                                    {brand.product_count} / {formatProductCap(brand.max_products)} products scoring
                                </span>
                                <span>max {intervalLabel(brand.max_pdp_score_interval).toLowerCase()} refresh</span>
                            </span>
                        ) : undefined
                    }
                />

                {summary && (
                    <div className="overflow-x-auto mb-4">
                        <table className="w-full border-collapse text-[12.5px] text-text-1">
                            <thead>
                                <tr>
                                    {["Month", "Audits", "Reports", "Prep packs", "Our cost", "Modeled", "Price"].map((h, i) => (
                                        <th
                                            key={h}
                                            className={`px-3 py-2 text-[10.5px] font-semibold text-text-2 uppercase tracking-[0.5px] border-b border-border-token bg-surface-2 ${
                                                i === 0 ? "text-left" : "text-right"
                                            }`}
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {summary.series.map((m) => (
                                    <tr key={m.month}>
                                        <td className="px-3 py-2 border-b border-border-token font-mono">{m.month}</td>
                                        <td className="px-3 py-2 border-b border-border-token text-right font-mono">{m.lines.audit.count}</td>
                                        <td className="px-3 py-2 border-b border-border-token text-right font-mono">{m.lines.catalog_report.count}</td>
                                        <td className="px-3 py-2 border-b border-border-token text-right font-mono">{m.lines.prep_pack.count}</td>
                                        <td className="px-3 py-2 border-b border-border-token text-right font-mono">{money(m.totals.actual_cost)}</td>
                                        <td className="px-3 py-2 border-b border-border-token text-right font-mono text-text-2">{money(m.totals.modeled_cost)}</td>
                                        <td className="px-3 py-2 border-b border-border-token text-right font-mono font-semibold">{money(m.totals.price)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="flex items-center gap-2 pb-3 text-[12px] flex-wrap">
                    <UsagePeriodPicker />
                    <span className="text-text-2 ml-2">Show:</span>
                    {[
                        ["", "All"],
                        ["audit", "Audits"],
                        ["catalog_report", "Report syntheses"],
                        ["prep_pack", "Prep packs"],
                        ["consumer_voice", "Consumer Voice"],
                        ["consumer_voice_report", "CV syntheses"],
                    ].map(([value, label]) => (
                        <button
                            key={value || "all"}
                            type="button"
                            onClick={() => {
                                setFilter("kind", value || null);
                                setPage(1);
                            }}
                            className={`px-2.5 py-1 rounded-md border ${
                                kind === value
                                    ? "border-accent text-text-1 font-semibold bg-tint"
                                    : "border-border-token text-text-2"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                    <span className="ml-auto text-text-2">
                        <Link to="/admin/pricing" className="hover:text-accent-text">
                            Rate card
                        </Link>
                    </span>
                </div>

                <DataTable<BrandUsageEvent>
                    columns={columns}
                    rows={list.items}
                    loading={list.loading}
                    totalCount={list.totalCount}
                    hasMore={list.hasMore}
                    error={list.error}
                    onLoadMore={list.loadMore}
                    emptyMessage="No usage in this period"
                    getRowKey={(r) => r.id}
                />
            </MainContainer>
        </WhiteBackground>
    );
}
